import React, { useContext } from 'react'
import './index.scss'
import { StoresContext } from '@App/contexts'
import {
    createRightsholderFormFields,
    createRightsholderFormSchema,
} from '@Entities/schemas/rightsholder/create-rightsholder'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormFieldFromProps from '@Fields/v2/FormFieldFromProps'
import PhoneNumberForm from '@Fields/v2/PhoneNumberForm'
import IdentifiersSection from '@Scenes/RightsholderRegistrationLayout/RightsholderRegistrationForm/IdentifiersSection'
import MemberDetailsSection from '@Scenes/RightsholderRegistrationLayout/RightsholderRegistrationForm/MemberDetailsSection'
import Button from '@Components/Buttons/Button'
import { ICONS } from '@Const/common'
import { useLocation, useNavigate } from 'react-router'
import Navigation from '@App/navigation'
import CheckboxForm from '@Fields/v2/CheckboxForm'
import FormFieldProps, { FormFields, isFormFields } from '@App/forms/v2/common/form-field'
import {
    CreateRightsholderForm,
    CreateRightsholderFormWithInputPhone,
} from '@Entities/rightsholder/create-rightsholder-dto'
import { observer } from 'mobx-react'
import RightsholderAddress from '@Scenes/RightsholderRegistrationLayout/RightsholderRegistrationForm/RightsholderAddress'
import RightsholderRegistrationSceneHeader from '@Scenes/RightsholderRegistrationLayout/RightsholderRegistrationSceneHeader'
import { verifyRightsholder } from '@App/api/routes/rightsholder/rightsholder'
import convertEmptyValuesToUndefined from '@App/forms/v2/helpers/convert-empty-values-to-undefined'
import IdentifiersToolTip from '@Scenes/RightsholderRegistrationLayout/RightsholderRegistrationForm/IdentifiersSection/IdentifiersToolTip'
import { RequiredAddressType } from '@Entities/schemas/common/address-schema'
import { PhoneNumberType } from '@Entities/schemas/common/phone-number-schema'

const RightsholderRegistrationForm = () => {
    const navigate = useNavigate()
    const { preloaderStore, createRightsholderStore } = useContext(StoresContext)
    const location = useLocation()

    const methods = useForm<CreateRightsholderFormWithInputPhone, unknown, CreateRightsholderForm>({
        mode: 'onChange',
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        delayError: 750,
        resolver: zodResolver(createRightsholderFormSchema),
        defaultValues: { memberType: location.state?.memberType || undefined },
    })

    const onSubmit = async (data: CreateRightsholderForm) => {
        preloaderStore.openTransparentPreloader(true)
        data = convertEmptyValuesToUndefined(data, true)
        data.address = convertEmptyValuesToUndefined(data.address, true)
        data.phoneNumber = convertEmptyValuesToUndefined(data.phoneNumber, true)
        if (data.hfaPublisherNumber) {
            try {
                const doesUserAlreadyHaveMemberWithHfaPublisherNumber = await verifyRightsholder(
                    data.hfaPublisherNumber
                )
                if (doesUserAlreadyHaveMemberWithHfaPublisherNumber.data) {
                    methods.setError('hfaPublisherNumber', {
                        type: 'manual',
                        message: 'This Publisher Number is already in use.',
                    })
                    preloaderStore.openTransparentPreloader(false)
                    return
                }
            } catch (ignore) {
                methods.setError('hfaPublisherNumber', {
                    type: 'manual',
                    message: 'No account found with Publisher Account Number.',
                })
                preloaderStore.openTransparentPreloader(false)
                return
            }
        }
        createRightsholderStore.setCreateRightsholderForm(data)
        navigate(Navigation.registrationRightsholder.questionnaire())
        preloaderStore.openTransparentPreloader(false)
    }

    const {
        memberType,
        memberSubType,
        name,
        hfaPublisherNumber,
        ipiNumber,
        isni,
        address,
        phoneNumber,
        email,
        termsAndConditions,
    } = createRightsholderFormFields

    if (!isFormFields(address) || !isFormFields(phoneNumber)) {
        throw new Error('Invalid rightsholder registration form configuration')
    }

    return (
        <>
            <RightsholderRegistrationSceneHeader />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <div className="register-rightsholder-form">
                        <MemberDetailsSection
                            name={name as FormFieldProps}
                            memberType={memberType as FormFieldProps}
                            memberSubType={memberSubType as FormFieldProps}
                        />
                        <IdentifiersSection
                            hfaPublisherNumber={hfaPublisherNumber as FormFieldProps}
                            isni={isni as FormFieldProps}
                            ipiNumber={ipiNumber as FormFieldProps}
                            identifierToolTip={<IdentifiersToolTip />}
                        />
                        <RightsholderAddress address={address as FormFields<RequiredAddressType>} />

                        <h5>Member Contact</h5>
                        <PhoneNumberForm phoneNumberFormFields={phoneNumber as FormFields<PhoneNumberType>} />
                        <FormFieldFromProps field={email} />
                        <CheckboxForm {...(termsAndConditions as FormFieldProps)}>
                            <>
                                <a
                                    href="https://www.themlc.com/portaltermsofuse"
                                    target="_blank"
                                    className="small"
                                    rel="noreferrer"
                                >
                                    Terms of Use
                                </a>{' '}
                                <strong className="blue-text">Required</strong>
                            </>
                        </CheckboxForm>
                        <Button type="primary" icon={ICONS.arrow_forward} mainColor submitType="submit">
                            Next
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </>
    )
}

export default observer(RightsholderRegistrationForm)
